import {ButtonWithIcon} from "components/ButtonWithIcon";
import {Exist} from "components/Exist";
import {HOCModal} from "components/HOCModal";
import {HOCModalCenterDesktopPosition} from "components/HOCModalCenterDesktopPosition";
import {setSecondTutorialViewState} from "modules/actions/user";
import {RequestState} from "modules/enums";
import {
	getUserStore,
	isSecondTutorialViewed,
	isTutorialViewed,
	loadingUserState,
} from "modules/selectors";
import {useAdobeDTM, useIsMobile} from "modules/utils/hooks";
import React, {Fragment, useCallback, useEffect} from "react";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import styled from "@emotion/styled";
import DOMPurify from "dompurify";
import RallySSO from "modules/utils/SSO/rallySSO";
import {getLogoAccordingState} from "modules/utils/dom_helpers";
import {redirectForSponsor} from "modules/utils/mlbConfig";

const Wrapper = styled.div`
	overflow-y: auto;
	overflow-x: hidden;
	height: 100%;
`;

const Controls = styled.div`
	> button {
		margin: 0 auto 30px auto;
		display: block;
		background: transparent;
		border: none;

		img {
			max-width: 125px;
		}
	}
`;

const Title = styled.p`
	font-weight: 800;
	font-size: 30px;
	line-height: 28px;
	text-align: center;
`;

const Description = styled.div`
	text-align: left;
	color: #ffff;
	font-size: 14px;
	max-width: 90%;
	width: 100%;
	padding: 20px;
	margin: 0 auto;

	ul {
		padding: 0 20px;

		li {
			list-style: disc;
			line-height: 1.8;
		}

		a {
			color: #2687ff;
			font-weight: bold;
		}
	}
`;

const Buttons = styled.div`
	display: flex;
	position: relative;
	bottom: 20px;
	padding-top: 40px;
`;

const NextButton = styled.button`
	background: #2687ff;
	color: #ffffff;
`;

export const TutorialSecondModal: React.FC = () => {
	const dispatch = useDispatch();

	const isViewed = useSelector(isTutorialViewed);
	const isSecondViewed = useSelector(isSecondTutorialViewed);
	const loadState = useSelector(loadingUserState);
	const i18n = useTranslation();
	const isMobile = useIsMobile();
	const {countryCode, stateCode} = useSelector(getUserStore);

	const isOpen = [isViewed, !isSecondViewed].every(Boolean);
	const isHidden = [!isOpen, loadState !== RequestState.RECEIVED].some(Boolean);
	const {trackState, trackAction} = useAdobeDTM();

	const prepareLink = (event: React.SyntheticEvent<HTMLDivElement>) => {
		event.stopPropagation();
		event.preventDefault();

		const target = event.target as HTMLAnchorElement;
		const href = target.getAttribute("href");
		if (target.tagName.toLowerCase() === "a" && href) {
			if (RallySSO.IS_NATIVE) {
				RallySSO.nativeBridge?.launchExternal(href);
			} else {
				window.open(href, "_blank");
			}
		}
	};

	useEffect(() => {
		if (!isHidden) {
			trackState({
				"page.name": "mlb play : beat the streak : latest updates",
				"page.subType": "latest updates",
			});
		}
	}, [isHidden, trackState]);

	const handleClick = useCallback(() => {
		redirectForSponsor(countryCode);
	}, [countryCode]);

	const onPlay = () => {
		trackAction({
			"page.name": "mlb play : beat the streak : latest updates",
			"action.name": "mlb play : mlb play : beat the streak : latest updates : play click",
			"action.element.text": "play",
		});
		dispatch(setSecondTutorialViewState());
	};

	if (isHidden) {
		return null;
	}

	const Content = () => (
		<Wrapper>
			<Controls>
				<button onClick={handleClick}>
					<img
						src={getLogoAccordingState(countryCode, stateCode)}
						alt="The main logo of MLB"
					/>
				</button>

				<Title>{i18n.t("tutorial_second.content.title", "Whats New")}</Title>

				<Description onClick={prepareLink}>
					<ul
						// eslint-disable-next-line react/no-danger
						dangerouslySetInnerHTML={{
							__html: DOMPurify.sanitize(i18n.t("tutorial_second.content.body")),
						}}
					/>
				</Description>

				<Buttons>
					<ButtonWithIcon
						text={i18n.t("tutorial_second.header.play", "Done")}
						styleExtend={NextButton}
						onClick={onPlay}
					/>
				</Buttons>
			</Controls>
		</Wrapper>
	);

	const props = {
		isOpen: true,
		onClose: onPlay,
		name: "second-tutorial",
		headerText: <Fragment>{i18n.t("tutorial_second.header.title", "Latest Updates")}</Fragment>,
	};

	return (
		<Fragment>
			<Exist when={isMobile}>
				<HOCModal {...props}>
					<Content />
				</HOCModal>
			</Exist>
			<Exist when={!isMobile}>
				<HOCModalCenterDesktopPosition {...props}>
					<Content />
				</HOCModalCenterDesktopPosition>
			</Exist>
		</Fragment>
	);
};
